import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Camera, Trash2, Loader2, Heart, Smile } from "lucide-react";
import AlbumButton from "@/components/PhotoAlbumButton";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Réduit la photo (1600 px max) avant l'envoi pour les téléphones.
function shrink(file) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, 1600 / Math.max(img.width, img.height));
      const cv = document.createElement("canvas");
      cv.width = Math.round(img.width * k); cv.height = Math.round(img.height * k);
      cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
      cv.toBlob((b) => resolve(b || file), "image/jpeg", 0.85);
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
}

function ShareForm({ patient, onShared }) {
  const [others, setOthers] = useState([]);
  const [ids, setIds] = useState([patient.id]);
  const [caption, setCaption] = useState("");
  const [consent, setConsent] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : ""), [file]);
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);
  useEffect(() => { api.get("/patients").then((r) => setOthers(r.data.filter((p) => p.id !== patient.id))).catch(() => {}); }, [patient.id]);
  const toggle = (id) => setIds((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const send = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("patient_ids", ids.join(","));
      fd.append("caption", caption.trim());
      fd.append("consent", consent ? "true" : "");
      fd.append("file", await shrink(file), "photo.jpg");
      await api.post("/workshop-photos", fd);
      toast.success("Photo partagée avec la famille");
      setFile(null); setCaption(""); setConsent(false); setIds([patient.id]); onShared();
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible."); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-dashed border-gray-300 p-4 mb-5 space-y-3" data-testid="photo-share-form">
      <input ref={input} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="photo-file-input" />
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" className="rounded-lg" onClick={() => input.current?.click()} data-testid="photo-pick-button">
          <Camera className="w-4 h-4 mr-2" /> {file ? "Changer de photo" : "Choisir une photo"}
        </Button>
        {preview && <img src={preview} alt="" className="h-16 w-24 object-cover rounded-lg border" data-testid="photo-preview" />}
      </div>
      <Input value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={200} placeholder="Légende (facultatif) : atelier peinture, goûter…" data-testid="photo-caption-input" />
      {others.length > 0 && (
        <div>
          <p className="text-sm text-gray-600 mb-1">Autres résidents présents sur la photo (leur famille la verra aussi) :</p>
          <div className="flex flex-wrap gap-3">
            {others.map((o) => (
              <label key={o.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <Checkbox checked={ids.includes(o.id)} onCheckedChange={() => toggle(o.id)} data-testid={`photo-resident-${o.id}`} /> {o.name}
              </label>
            ))}
          </div>
        </div>
      )}
      <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
        <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="photo-consent-checkbox" className="mt-0.5" />
        J'ai l'accord de chaque personne visible sur la photo, ou de sa famille (droit à l'image).
      </label>
      <Button disabled={!file || !consent || busy} onClick={send} className="rounded-lg bg-emerald-700 hover:bg-emerald-800" data-testid="photo-share-button">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Partager avec la famille
      </Button>
      <p className="text-xs text-gray-400">Les photos sont gardées 3 mois, puis supprimées automatiquement.</p>
    </div>
  );
}

export default function WorkshopPhotosCard({ patient }) {
  const [photos, setPhotos] = useState([]);
  const load = () => api.get("/workshop-photos", { params: { patient_id: patient.id } }).then((r) => setPhotos(r.data)).catch(() => {});
  useEffect(() => { load(); }, [patient.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (ph) => {
    if (!window.confirm("Supprimer cette photo pour toutes les familles ?")) return;
    try { await api.delete(`/workshop-photos/${ph.id}`); load(); toast.success("Photo supprimée"); } catch { toast.error("Suppression impossible."); }
  };
  return (
    <Card className="border-gray-200 mt-8" data-testid="workshop-photos-card">
      <CardContent className="p-6">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2"><Camera className="w-4 h-4 text-amber-600" /> Photos des ateliers</h3>
        <p className="text-sm text-gray-500 mb-4">Partagez quelques photos avec la famille de {patient.name}.</p>
        <ShareForm patient={patient} onShared={load} />
        {photos.length > 0 && <AlbumButton url={`/patients/${patient.id}/photos/album/pdf`} name={patient.name} testId="caregiver-photo-album" className="mb-4" />}
        {photos.length === 0 ? (
          <p className="text-gray-400 py-4 text-center" data-testid="photos-empty">Aucune photo partagée.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="photos-grid">
            {photos.map((ph) => (
              <div key={ph.id} className="rounded-xl border border-gray-200 overflow-hidden" data-testid={`photo-item-${ph.id}`}>
                <img src={`${BACKEND}${ph.image_url}`} alt={ph.caption} className="w-full h-36 object-cover" />
                <div className="p-2 text-xs text-gray-600 space-y-1">
                  {ph.caption && <p className="font-medium text-gray-800">{ph.caption}</p>}
                  <p>{ph.patient_names.join(", ")}</p>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1">
                      <Heart className="w-3.5 h-3.5 text-rose-400" /> {ph.reactions.filter((r) => r === "coeur").length}
                      <Smile className="w-3.5 h-3.5 text-amber-500 ml-2" /> {ph.reactions.filter((r) => r === "sourire").length}
                    </span>
                    <button onClick={() => remove(ph)} className="text-red-500 hover:text-red-700" data-testid={`photo-delete-${ph.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
